import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { User, Mail, Lock, ArrowLeft } from 'lucide-react';

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();
  const params = new URLSearchParams(location.search);
  const redirect = params.get('redirect');
  const serviceId = params.get('service');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    hair_type: 'Normal',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await register(formData);
      if (result.requires_confirmation) {
        toast.success('Bestätigungs-E-Mail gesendet. Bitte prüfen Sie auch Ihren Spam-Ordner.');
        const loginParams = new URLSearchParams({ confirmation: 'pending' });
        if (redirect) loginParams.set('redirect', redirect);
        if (serviceId) loginParams.set('service', serviceId);
        navigate(`/login?${loginParams.toString()}`);
      } else {
        toast.success('Erfolgreich registriert und angemeldet!');
        if (redirect === 'angebote') {
          navigate('/angebote');
        } else {
          const bookingQuery = redirect === 'booking'
            ? `?booking=open${serviceId ? `&service=${encodeURIComponent(serviceId)}` : ''}`
            : '';
          navigate(`/${bookingQuery}`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6 text-slate-600 hover:text-blue-600"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück zur Startseite
        </Button>

        <Card className="p-8 shadow-xl border-slate-100">
          <div className="text-center mb-8">
            <div className="mx-auto mb-4 flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_friseur-booking/artifacts/mtmj2lif_IMG_5702.PNG" 
                alt="Salon Galo Logo" 
                className="h-16 w-auto"
              />
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Konto erstellen</h1>
            <p className="text-slate-600 mt-2">Mit E-Mail registrieren und Termine online buchen</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                Name
              </Label>
              <div className="relative mt-2">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="pl-10 h-12 rounded-lg border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Max Mustermann"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                E-Mail
              </Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 h-12 rounded-lg border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ihre@email.de"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                Passwort
              </Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 h-12 rounded-lg border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Mindestens 8 Zeichen"
                  minLength={8}
                  required
                  data-testid="password-input"
                />
              </div>
              <p className="mt-2 text-xs text-slate-500">Mindestens 8 Zeichen</p>
            </div>

            <div>
              <Label htmlFor="hair_type" className="text-sm font-medium text-slate-700">
                Haartyp (für personalisierte Pflegeberatung)
              </Label>
              <select
                id="hair_type"
                value={formData.hair_type}
                onChange={(e) => setFormData({ ...formData, hair_type: e.target.value })}
                className="w-full h-12 mt-2 px-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="hair-type-select"
              >
                <option value="Normal">Normal</option>
                <option value="Trocken">Trocken</option>
                <option value="Fein">Fein</option>
                <option value="Glatt">Glatt</option>
                <option value="Fettend">Fettend</option>
                <option value="Schuppig">Schuppig</option>
                <option value="Strapaziert">Strapaziert</option>
              </select>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 rounded-full text-base font-medium shadow-lg hover:shadow-blue-200 transition-all"
              data-testid="register-submit-button"
            >
              {loading ? 'Registrieren...' : 'Registrieren'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Bereits ein Konto?{' '}
              <Link
                to={`/login${location.search}`}
                className="text-blue-600 hover:text-blue-700 font-medium"
                data-testid="login-link"
              >
                Jetzt anmelden
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
